import type { ComponentProps } from "react";
import { LibraryBig } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LibraryBulkBar } from "@/components/library/LibraryBulkBar";
import { LibraryItemCard } from "@/components/library/LibraryItemCard";
import { LibraryItemDetail } from "@/components/library/LibraryItemDetail";
import { libraryCopy } from "@/lib/library";
import type { LibraryRecord } from "@/components/library/myLibraryHelpers";

type DetailProps = ComponentProps<typeof LibraryItemDetail>;
type BulkBarProps = ComponentProps<typeof LibraryBulkBar>;
type CardProps = ComponentProps<typeof LibraryItemCard>;

type Props = {
  selectedItem: LibraryRecord | null;
  loading: boolean;
  filteredItems: LibraryRecord[];
  filteredItemNoun: { singular: string; plural: string };
  itemsCount: number;
  linkedExcerpts: DetailProps["linkedExcerpts"];
  returnTo: string;
  myLibraryReturnTo: string;
  onReturnToOrigin: () => void;
  onCloseItemDetail: () => void;
  onOpenForEdit: (item: LibraryRecord) => void;
  onCreateExcerpt: (item: LibraryRecord) => void;
  onViewExcerpts: (item: LibraryRecord) => void;
  onDeleteSelectedItem: (item: LibraryRecord) => void;
  onChangeStatus: NonNullable<CardProps["onChangeStatus"]>;
  onOpenItemDetail: (id: string) => void;
  selectMode: boolean;
  selectedItemIds: Set<string>;
  allVisibleSelected: boolean;
  bulkBusy: boolean;
  categories: BulkBarProps["categories"];
  onToggleSelectAll: () => void;
  onExitSelectionMode: () => void;
  onToggleItemSelection: (id: string) => void;
  onAssignCategories: BulkBarProps["onAssignCategories"];
  onRemoveCategories: BulkBarProps["onRemoveCategories"];
  onSetStatus: BulkBarProps["onSetStatus"];
  onBulkDelete: BulkBarProps["onDelete"];
  onCreateCategory: BulkBarProps["onCreateCategory"];
};

/**
 * The main "what's in this section" body of the My Library page:
 * the selected item detail OR the loading state OR the empty state OR the list.
 * Lifted out of MyLibrary.tsx to keep the page component focused on orchestration.
 */
export const MyLibraryListSection = ({
  selectedItem, loading, filteredItems, filteredItemNoun, itemsCount,
  linkedExcerpts, returnTo, myLibraryReturnTo,
  onReturnToOrigin, onCloseItemDetail,
  onOpenForEdit, onCreateExcerpt, onViewExcerpts, onDeleteSelectedItem, onChangeStatus, onOpenItemDetail,
  selectMode, selectedItemIds, allVisibleSelected, bulkBusy, categories,
  onToggleSelectAll, onExitSelectionMode, onToggleItemSelection,
  onAssignCategories, onRemoveCategories, onSetStatus, onBulkDelete, onCreateCategory,
}: Props) => {
  if (selectedItem) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/10 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Source detail</p>
            <p className="text-sm text-muted-foreground">
              Review the full source record, then jump into excerpt work or editing only when needed.
            </p>
          </div>
          <Button variant="outline" onClick={returnTo ? onReturnToOrigin : onCloseItemDetail}>
            {returnTo ? "Return to my source/excerpt view" : "Back to library"}
          </Button>
        </div>
        <LibraryItemDetail
          item={selectedItem}
          linkedExcerpts={linkedExcerpts}
          onEdit={() => onOpenForEdit(selectedItem)}
          onCreateExcerpt={() => onCreateExcerpt(selectedItem)}
          onViewExcerpts={() => onViewExcerpts(selectedItem)}
          onDelete={() => onDeleteSelectedItem(selectedItem)}
          onChangeStatus={onChangeStatus}
          returnTo={myLibraryReturnTo}
        />
      </div>
    );
  }
  if (loading) {
    return (
      <Card className="shadow-soft">
        <CardContent className="py-10 text-sm text-muted-foreground">Loading your library…</CardContent>
      </Card>
    );
  }
  if (filteredItems.length === 0) {
    return (
      <Card className="shadow-soft">
        <CardContent className="py-16 text-center space-y-3">
          <LibraryBig className="mx-auto h-10 w-10 text-muted-foreground" />
          <h2 className="font-serif text-xl">
            {itemsCount === 0 ? libraryCopy.emptyTitle : `No ${filteredItemNoun.plural} match the current filters`}
          </h2>
          <p className="mx-auto max-w-md text-muted-foreground">
            {itemsCount === 0
              ? libraryCopy.emptyDescription
              : "Try another search, change the sort, or clear one of the active filters."}
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      {selectMode ? (
        <LibraryBulkBar
          allSelected={allVisibleSelected}
          selectedCount={selectedItemIds.size}
          categories={categories}
          disabled={bulkBusy}
          onToggleSelectAll={onToggleSelectAll}
          onClear={onExitSelectionMode}
          onAssignCategories={onAssignCategories}
          onRemoveCategories={onRemoveCategories}
          onSetStatus={onSetStatus}
          onDelete={onBulkDelete}
          onCreateCategory={onCreateCategory}
        />
      ) : null}
      <div className="space-y-3">
        {filteredItems.map((item) => (
          <LibraryItemCard
            key={item.id}
            item={item}
            selectMode={selectMode}
            isSelected={selectedItemIds.has(item.id)}
            onToggleSelect={onToggleItemSelection}
            onEdit={onOpenForEdit}
            onCreateExcerpt={onCreateExcerpt}
            onView={onOpenItemDetail}
            onViewExcerpts={onViewExcerpts}
            onChangeStatus={onChangeStatus}
          />
        ))}
      </div>
    </div>
  );
};
